import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Coins, Gift, Share2, Copy, Check, ChevronDown, ShoppingBag, Clock, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { rewards, type CoinsResponse, type ReferralResponse, type CoinTransaction, type ReferralEntry } from '../api/client';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import RankHeroCard from '../components/RankHeroCard';
import RankBenefits from '../components/RankBenefits';
import RankComparison from '../components/RankComparison';
import CoinStatement from '../components/CoinStatement';
import CoinExpiryStatement from '../components/CoinExpiryStatement';
import { getBadgeForOrders, nextTier } from '../lib/badges';
import { useInfiniteList, rowCursor } from '../hooks/useInfiniteList';

const FAQS: { q: string; a: string }[] = [
  { q: 'How do I earn ZS Coins?', a: 'You earn ZS Coins back on every delivered order — 1% to 3% of the order value depending on your rank, so higher ranks earn more. For example, at 1% a ₹1,000 delivered order earns 10 coins (worth ₹10).' },
  { q: 'What is a ZS Coin worth?', a: 'Each coin is worth ₹1 at checkout. You can redeem coins to pay for up to 20% of an order’s value — there is no minimum.' },
  { q: 'Do coins expire?', a: 'Yes — coins expire 12 months after they are earned. We always spend your soonest-to-expire coins first.' },
  { q: 'What happens if I cancel or return an order?', a: 'Coins you already spent are not refunded. Coins earned from an order that is cancelled or returned are reversed. Referral rewards tied to that order are also reversed.' },
  { q: 'How does referral work?', a: 'Share your code or link. When a friend signs up with it they instantly get a pending bonus that unlocks after their first delivered order — and you get rewarded once that first order is delivered.' },
  { q: 'Can I use more than one referral code?', a: 'No. A referral code can be used once, by a new account, and only before your first delivered order.' },
];

export default function RewardsPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [coins, setCoins] = useState<CoinsResponse | null>(null);
  const [ref, setRef] = useState<ReferralResponse | null>(null);
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [redeemMsg, setRedeemMsg] = useState('');
  const [redeemErr, setRedeemErr] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [coinTab, setCoinTab] = useState<'statement' | 'expiring'>('statement');

  // Lists paginate (15/page) and load more on scroll; the summary (balance,
  // pending, stats, code) is captured from each list's first page.
  const resetKey = user ? `${user.id}:${refreshKey}` : null;
  const coinActivity = useInfiniteList<CoinTransaction>(
    async (cursor) => {
      const r = await rewards.coins(cursor);
      if (!cursor) setCoins(r);
      return { items: r.history, hasMore: r.has_more };
    },
    (t) => rowCursor(t.created_at, t.id),
    resetKey,
    !!user,
  );
  const invites = useInfiniteList<ReferralEntry>(
    async (cursor) => {
      const r = await rewards.referral(cursor);
      if (!cursor) setRef(r);
      return { items: r.referrals, hasMore: r.has_more };
    },
    (r) => rowCursor(r.created_at, r.id),
    resetKey,
    !!user,
  );

  useEffect(() => {
    if (!user) { setCoins(null); setRef(null); }
  }, [user]);

  const badge = getBadgeForOrders(user?.order_count ?? 0);
  const next = nextTier(user?.order_count ?? 0);
  const coinValue = coins?.coin_value_inr ?? 1;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://zerosmoke.in';
  const link = ref?.code ? `${origin}/signup?ref=${ref.code}` : '';
  const shareText = ref?.code
    ? `Join ZeroSmoke with my code ${ref.code} — we both earn ZS Coins! ${link}`
    : '';

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';

  const copy = (text: string, which: 'link' | 'code') => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    });
  };
  const nativeShare = () => {
    if (navigator.share) navigator.share({ title: 'ZeroSmoke Rewards', text: shareText, url: link }).catch(() => {});
    else copy(link, 'link');
  };
  const submitCode = async () => {
    setRedeemMsg(''); setRedeemErr('');
    const code = codeInput.trim();
    if (!code) return;
    try {
      const r = await rewards.redeemReferral(code);
      setRedeemMsg(`Code applied! ${r.coins} coins added (pending until your first delivered order).`);
      setCodeInput('');
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      setRedeemErr(e?.message || 'Could not apply that code.');
    }
  };

  return (
    <div className={isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}>
      <SEO {...pageSeoMeta.rewards} />
      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Rewards' },
      ]} />

      {/* ── Rank benefits banner — full-bleed hero at the top ── */}
      <RankBenefits currentSlug={badge?.slug ?? null} isLoggedIn={!!user} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">

        {/* ── Intro — compact lead-in; the full-width banner above is the hero ── */}
        <header className="text-center">
          <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Earn as you shop
          </h1>
          <p className={`mt-2 max-w-2xl mx-auto text-sm ${sub}`}>
            Collect <strong>ZS Coins</strong> on every delivered order, climb the <strong>rank ladder</strong>,
            and earn bonus coins when you <strong>refer friends</strong>. Here’s exactly how it works.
          </p>

          {!user && (
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/signup" className="px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">Create account</Link>
              <Link to="/login" className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${card} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>Sign in</Link>
            </div>
          )}
        </header>

        {/* ── Your rank & coins (signed-in hero) ── */}
        {user && (
          <RankHeroCard
            badge={badge}
            orderCount={user.order_count}
            referrals={ref?.stats.qualified ?? 0}
            balance={coins?.balance ?? 0}
            coinValue={coinValue}
            expiring={coins?.expiring_this_month?.amount ?? 0}
            expiringOn={coins?.expiring_this_month?.on ?? null}
          />
        )}

        {/* ── How it works + coin value — 2×2 on mobile, 4-up on desktop ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { icon: ShoppingBag, t: 'Shop', d: 'Place an order and get it delivered.', grad: 'from-emerald-400 to-emerald-600', glow: 'rgba(16,185,129,0.40)', shadow: 'shadow-emerald-500/30' },
            { icon: Coins, t: 'Earn', d: '1%–3% back as ZS Coins by rank, plus referral bonuses.', grad: 'from-amber-400 to-amber-600', glow: 'rgba(245,158,11,0.40)', shadow: 'shadow-amber-500/30' },
            { icon: Gift, t: 'Redeem', d: 'Spend coins for up to 20% off your next order.', grad: 'from-fuchsia-400 to-purple-600', glow: 'rgba(168,85,247,0.40)', shadow: 'shadow-purple-500/30' },
            { icon: Coins, t: `1 ZS Coin = ₹${coinValue}`, d: 'Redeem for up to 20% off any order — no minimum.', grad: 'from-amber-400 to-amber-600', glow: 'rgba(245,158,11,0.40)', shadow: 'shadow-amber-500/30' },
          ].map((s, i) => (
            <div key={i} className={`rounded-2xl border p-4 sm:p-5 text-center ${card}`}>
              <div className="relative mx-auto mb-3 w-12 h-12">
                <div className="absolute inset-0 rounded-2xl blur-lg" style={{ background: s.glow }} aria-hidden />
                <div className={`relative w-12 h-12 rounded-2xl bg-gradient-to-br ${s.grad} flex items-center justify-center shadow-lg ${s.shadow} ring-1 ring-white/25`}>
                  <s.icon size={22} className="text-white" />
                </div>
              </div>
              <p className="font-bold text-sm sm:text-base leading-tight">{s.t}</p>
              <p className={`text-xs sm:text-sm mt-1 ${sub}`}>{s.d}</p>
            </div>
          ))}
        </div>

        {/* ── Ranks — overview + per-tier benefits (merged) ── */}
        <RankComparison currentSlug={badge?.slug ?? null} id="ranks" />

        {/* ── ZS Coins ── */}
        <section className={`rounded-2xl border p-6 ${card}`}>
          <div className="flex items-center gap-2 mb-2">
            <Coins size={18} className="text-emerald-500" />
            <h2 className="text-xl font-bold">ZS Coins</h2>
          </div>
          <ul className={`text-sm space-y-1.5 ${sub}`}>
            <li>• Earn <strong>1%–3% of your order value</strong> back as coins once an order is delivered — higher ranks earn more.</li>
            <li>• Each coin is worth <strong>₹1</strong>. Redeem for up to <strong>20%</strong> of an order — no minimum.</li>
            <li>• Coins <strong>expire 12 months</strong> after they’re earned (soonest-to-expire spent first).</li>
            <li>• Cancel/return reverses coins earned on that order; spent coins aren’t refunded.</li>
          </ul>

          {user ? (
            <div className="mt-5">
              <div className="flex flex-wrap items-end gap-6 mb-4">
                <div>
                  <p className={`text-xs uppercase tracking-wider ${sub}`}>Balance</p>
                  <p className="text-2xl font-extrabold text-emerald-500 inline-flex items-center gap-1.5"><span className="leading-none" aria-hidden="true">🪙</span>{coins?.balance ?? 0} <span className="text-base font-semibold">coins</span></p>
                  <p className={`text-xs ${sub}`}>≈ ₹{((coins?.balance ?? 0) * coinValue).toFixed(0)} to spend</p>
                </div>
                {!!(coins?.pending) && (
                  <div className={`text-sm flex items-center gap-1.5 ${sub}`}>
                    <Clock size={14} /> {coins.pending} pending <span className="text-xs">(unlock after first delivery)</span>
                  </div>
                )}
              </div>

              <Link
                to="/rewards/coins"
                className="mb-4 flex items-center justify-center gap-3 rounded-xl px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-sm transition-colors"
              >
                <Coins size={16} />
                <span className="text-sm font-semibold">Total ZS Coins savings</span>
                <span className="inline-flex items-center gap-1 text-base font-extrabold text-amber-300"><span className="leading-none" aria-hidden="true">🪙</span>{(coins?.total_earned ?? 0).toLocaleString('en-IN')}</span>
                <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center shrink-0"><ArrowRight size={15} /></span>
              </Link>

              <div className={`flex gap-1 mb-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                {([['statement', 'Statement'], ['expiring', 'Expiring']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setCoinTab(k)}
                    className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${coinTab === k ? 'border-emerald-500 text-emerald-500' : `border-transparent ${sub}`}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {coinTab === 'statement' ? (
                <CoinStatement items={coinActivity.items} loading={coinActivity.loading} onScroll={coinActivity.onScroll} />
              ) : (
                <CoinExpiryStatement resetKey={resetKey} />
              )}
            </div>
          ) : (
            <p className={`mt-4 text-sm ${sub}`}><Link to="/signup" className="text-emerald-500 font-semibold">Create an account</Link> to start earning coins.</p>
          )}
        </section>

        {/* ── Referral ── */}
        <section className={`rounded-2xl border p-6 ${card}`}>
          <div className="flex items-center gap-2 mb-2">
            <Gift size={18} className="text-fuchsia-500" />
            <h2 className="text-xl font-bold">Refer a friend</h2>
          </div>
          <p className={`text-sm ${sub}`}>
            Share your link. When a friend signs up with it they get <strong>{ref?.referee_bonus ?? 300} coins</strong> (unlocked after their
            first delivered order), and you get <strong>{ref?.referrer_bonus ?? 300} coins</strong> once that first order is delivered.
          </p>

          {user ? (
            <div className="mt-5 space-y-5">
              {/* share */}
              <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-800 bg-gray-950/40' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-xs uppercase tracking-wider mb-2 ${sub}`}>Your referral link</p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className={`flex-1 min-w-0 truncate text-sm px-3 py-2 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>{link || '…'}</code>
                  <button onClick={() => copy(link, 'link')} className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5">
                    {copied === 'link' ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => copy(ref?.code || '', 'code')} className={`px-3 py-1.5 rounded-lg border text-sm ${card} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>Code: <strong>{ref?.code}</strong></button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">WhatsApp</a>
                  <a href={`mailto:?subject=${encodeURIComponent('Join ZeroSmoke')}&body=${encodeURIComponent(shareText)}`} className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${card} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>Email</a>
                  <button onClick={nativeShare} className={`px-3 py-1.5 rounded-lg border text-sm font-semibold inline-flex items-center gap-1.5 ${card} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}><Share2 size={14} /> Share</button>
                </div>
              </div>

              {/* stats */}
              <div className="grid grid-cols-3 gap-3 text-center">
                {[['Invited', ref?.stats.invited ?? 0], ['Qualified', ref?.stats.qualified ?? 0], ['Coins earned', ref?.stats.coins_earned ?? 0]].map(([l, v]) => (
                  <div key={l} className={`rounded-xl border p-3 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <p className="text-xl font-extrabold text-emerald-500">{v}</p>
                    <p className={`text-xs ${sub}`}>{l}</p>
                  </div>
                ))}
              </div>

              {/* referral list */}
              <div>
                <p className="text-sm font-semibold mb-2">Your invites</p>
                <div onScroll={invites.onScroll} className={`rounded-xl border divide-y ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-200 divide-gray-100'} max-h-72 overflow-y-auto`}>
                  {invites.items.length ? invites.items.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div>
                        <p className="font-medium">{r.name}</p>
                        <p className={`text-xs ${sub}`}>{r.email}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        r.status === 'qualified' ? 'bg-emerald-500/15 text-emerald-500'
                        : r.status === 'reversed' ? 'bg-red-500/15 text-red-500'
                        : 'bg-amber-500/15 text-amber-500'}`}>{r.status}</span>
                    </div>
                  )) : <p className={`px-4 py-6 text-center text-sm ${sub}`}>No invites yet — share your link to start earning.</p>}
                  {invites.loading && invites.items.length > 0 && <p className={`px-4 py-3 text-center text-xs ${sub}`}>Loading…</p>}
                </div>
              </div>

              {/* redeem a code */}
              <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                <p className="text-sm font-semibold mb-2">Got a code from a friend?</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder="Enter code"
                    className={`flex-1 min-w-0 text-sm px-3 py-2 rounded-lg border outline-none ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`} />
                  <button onClick={submitCode} className="px-4 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold">Apply</button>
                </div>
                {redeemMsg && <p className="text-xs text-emerald-500 mt-2">{redeemMsg}</p>}
                {redeemErr && <p className="text-xs text-red-500 mt-2">{redeemErr}</p>}
              </div>
            </div>
          ) : (
            <p className={`mt-4 text-sm ${sub}`}><Link to="/signup" className="text-emerald-500 font-semibold">Sign up</Link> to get your referral link.</p>
          )}
        </section>

        {/* ── FAQ ── */}
        <section>
          <h2 className="text-xl font-bold mb-4">Rewards FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={i} className={`rounded-xl border ${card}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left font-medium">
                  {f.q}
                  <ChevronDown size={16} className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className={`px-4 pb-4 text-sm ${sub}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* ── Footer CTA ── */}
        {!user && (
          <div className="text-center">
            <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">
              Start earning <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
